import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/ui';
import { StoreCardVisual } from '@/components/store/StoreCardVisual';
import { formatCOP } from '@/lib/format';
import { categoryTheme } from '@/lib/categoryTheme';
import type { PartnerProduct } from '@/hooks/usePartnerProducts';

/**
 * StoreProductCard — card premium de producto partner para la Tienda (`/store`).
 * La zona superior es un bloque de icono semántico (`StoreCardVisual`) por
 * categoría — SIN fotos. Conserva EXACTAMENTE los datos y las dos acciones
 * reales: "Detalle" (→ `/store/:id`) y "Agregar" (add-to-cart vía `onAdd`).
 */

interface StoreProductCardProps {
  product: PartnerProduct;
  onAdd: (product: PartnerProduct) => void;
}

export function StoreProductCard({ product, onAdd }: StoreProductCardProps): JSX.Element {
  const theme = categoryTheme(product.category);

  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm transition duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lg">
      <StoreCardVisual category={product.category} name={product.title}>
        <Badge tone={theme.tone} className="absolute left-3 top-3 shadow-sm">
          {theme.label}
        </Badge>
      </StoreCardVisual>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-display text-base font-semibold text-fc-ink">
          {product.title}
        </h3>
        <p className="mt-1 line-clamp-1 text-xs text-fc-muted">{product.partnerName}</p>
        {product.description && (
          <p className="mt-2 line-clamp-2 text-sm text-fc-muted">{product.description}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-fc-line/70 pt-4">
          <span className="font-display text-base font-semibold tabular-nums text-fc-ink">
            {formatCOP(product.priceCents)}
          </span>
          <div className="flex gap-2">
            <Button as={Link} to={`/store/${product.id}`} variant="secondary" size="sm">
              Detalle
            </Button>
            <Button variant="primary" size="sm" onClick={() => onAdd(product)}>
              Agregar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
